/**
 * The body of a contact-form submission.
 *
 * A pure mapping from what the visitor typed plus the widget record to the
 * shape the forms endpoint expects. Split out of client.ts because the
 * server's field names carry a lot of history that is worth reading on its
 * own — and because a payload this particular deserves to be testable without
 * driving a whole submit.
 */
import type { ChatType, ContactFormInput, WidgetConfig } from '../../types';

export interface ContactFormRequestInput {
  /** Already normalised: known fields filled in, phone numbers reformatted. */
  input: ContactFormInput;
  config: WidgetConfig | null;
  chatType: ChatType;
  locationId: string;
  widgetId?: string;
  /** The captcha token this submit is spending, if it has one. */
  captchaToken?: string;
  /** Present once a thread exists; the form starts one for a live chat. */
  conversationId?: string;
}

export function contactFormRequestBody({
  input,
  config,
  chatType,
  locationId,
  widgetId,
  captchaToken,
  conversationId,
}: ContactFormRequestInput): Record<string, unknown> {
  return {
    ...input,
    captchaToken,
    locationId,

    // The token is minted for a widget, and the server verifies the
    // submission against it. Omitting this is answered with
    // "requestToken verification failed" — a 401 that reads as a token
    // problem when the token is fine.
    chatWidgetId: widgetId,

    // Two submit paths in the widget send different values here, and this one
    // method serves both. A standalone form sends its own chat type
    // (text-widget.tsx: chatType: this.chatTypeState); the contact form shown
    // inside a live chat sends the literal 'Live_Chat' (chat-form.tsx).
    // Copying the latter for every case mislabels an emailChat submission.
    chatType: chatType === 'liveChat' ? 'Live_Chat' : chatType,

    // Rendered verbatim by the host, so the server is told what the visitor
    // was actually shown.
    legalNoticeText: config?.compliance.consentText || undefined,
    conversationId,
    subType: chatType === 'allInOneChat' ? 'ALL_IN_ONE_CHAT' : undefined,
    enableContactForm: config?.contactForm.enabled || undefined,
    termsAndConditions: config?.compliance.consentText,
    // Only meaningful when a checkbox was actually shown; otherwise the
    // server is told nothing rather than told "false".
    consentGiven: config?.compliance.showConsentCheckbox ? !!input.consentGiven : undefined,
    consentRequired: config?.compliance.consentRequired || undefined,
  };
}

/**
 * The submitted details as transcript lines ("Name: Ada"), which the host
 * echoes into the thread as the visitor's first message so the conversation
 * reads as one. Fields the visitor left empty are skipped.
 */
export function contactFormSummaryLines(
  fields: Array<Record<string, unknown>>,
  input: Record<string, unknown>,
): string[] {
  return fields
    .map(field => {
      const key = typeof field.value === 'string' ? field.value : '';
      const value = key ? input[key] : undefined;
      return key && value ? `${(field.label as string) || key}: ${String(value)}` : '';
    })
    .filter(Boolean);
}
